/**
 * Pagina "Il mio profilo" — l'utente corrente visualizza e modifica
 * i propri dati anagrafici e cambia la password.
 *
 * Chiama PATCH /api/v1/auth/me per i dati personali
 * e PATCH /api/v1/utenti/me/password per il cambio password.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, type UtenteCorrente, type MenuMode } from './AuthContext';
import AvatarUploader from './AvatarUploader';
import TwoFactorSettings from './TwoFactorSettings';
import { LINGUE_SUPPORTATE, setLanguageFromUser } from '../i18n';

/** Timezone selezionabili (allineati alla whitelist backend). */
const TIMEZONE_OPZIONI = [
  'Europe/Rome',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'UTC',
  'America/New_York',
];

/** Modalita' di apertura sidebar (preferenza utente). */
const MENU_MODE_OPZIONI = [
  { value: 'tutto_aperto', label: 'Tutto aperto - gruppi sempre espansi' },
  { value: 'tutto_chiuso', label: 'Tutto chiuso - apri quanti gruppi vuoi' },
  { value: 'fisarmonica', label: 'Fisarmonica - un gruppo aperto per volta' },
  { value: 'libera', label: 'Libera - apri/chiudi, stato ricordato' },
];

/** Etichetta leggibile per la lingua. */
const LINGUA_LABEL: Record<string, string> = {
  'it-IT': 'Italiano (it-IT)',
  'en-US': 'English (en-US)',
};

/** Campi del form dati personali. */
interface DatiForm {
  nome: string;
  cognome: string;
  telefono: string;
  indirizzo: string;
  civico: string;
  cap: string;
  citta: string;
  provincia: string;
  data_nascita: string;
  codice_fiscale: string;
}

/** Campi del form cambio password. */
interface PasswordForm {
  password_attuale: string;
  nuova_password: string;
  conferma_password: string;
}

/** Converte il profilo utente nei valori del form. */
const userToForm = (u: UtenteCorrente): DatiForm => ({
  nome: u.nome ?? '',
  cognome: u.cognome ?? '',
  telefono: u.telefono ?? '',
  indirizzo: u.indirizzo ?? '',
  civico: u.civico ?? '',
  cap: u.cap ?? '',
  citta: u.citta ?? '',
  provincia: u.provincia ?? '',
  data_nascita: u.data_nascita ?? '',
  codice_fiscale: u.codice_fiscale ?? '',
});

const EMPTY_PASSWORD: PasswordForm = {
  password_attuale: '',
  nuova_password: '',
  conferma_password: '',
};

/* Stili condivisi — inline per non introdurre classi fuori da ui_core (regola 6.8). */
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 14,
  borderRadius: 6,
  border: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-background)',
  color: 'var(--color-foreground)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
  color: 'var(--color-foreground)',
};

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-card)',
  border: '1px solid var(--color-border)',
  borderRadius: 12,
  padding: 24,
};

const ProfiloUtente: React.FC = () => {
  const { user, reload } = useAuth();
  const { t } = useTranslation();

  /* Preferenze (lingua/timezone/menuMode) - salvataggio dedicato via PATCH /me. */
  const [lingua, setLingua] = useState<string>(user?.lingua ?? 'it-IT');
  const [timezone, setTimezone] = useState<string>(user?.timezone ?? 'Europe/Rome');
  const [menuMode, setMenuMode] = useState<string>(user?.menu_mode ?? 'libera');
  const [readingPane, setReadingPane] = useState<string>(user?.email_reading_pane ?? 'destra');
  const [avatarDocId, setAvatarDocId] = useState<string | null>(user?.avatar_documento_id ?? null);
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefMsg, setPrefMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  /* ─── State form dati ─── */
  const [dati, setDati] = useState<DatiForm>(user ? userToForm(user) : userToForm({} as UtenteCorrente));
  const [datiSaving, setDatiSaving] = useState(false);
  const [datiMsg, setDatiMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  /* ─── State form password ─── */
  const [pwd, setPwd] = useState<PasswordForm>(EMPTY_PASSWORD);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  /* Sincronizza form quando user cambia (es. dopo reload). */
  useEffect(() => {
    if (user) {
      setDati(userToForm(user));
      setLingua(user.lingua ?? 'it-IT');
      setTimezone(user.timezone ?? 'Europe/Rome');
      setMenuMode(user.menu_mode ?? 'libera');
      setReadingPane(user.email_reading_pane ?? 'destra');
      setAvatarDocId(user.avatar_documento_id ?? null);
    }
  }, [user]);

  if (!user) return null;

  const token = localStorage.getItem('token') ?? '';

  /* Salva preferenze lingua/timezone/menuMode (il reload allinea la lingua UI). */
  const handleSavePreferenze = async () => {
    setPrefSaving(true);
    setPrefMsg(null);
    try {
      const res = await fetch('/api/v1/auth/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lingua, timezone, menu_mode: menuMode, email_reading_pane: readingPane }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { detail?: string }).detail ?? `Errore ${res.status}`);
      }
      await reload();
      // Applica la lingua scelta DOPO il reload: la rilettura di /me potrebbe
      // essere stale (commit post-risposta di get_db) e riportare la vecchia
      // lingua; forzando qui il valore scelto lo switch e' immediato e corretto.
      setLanguageFromUser(lingua);
      setPrefMsg({ type: 'ok', text: t('profile.dataUpdated') });
    } catch (err) {
      setPrefMsg({ type: 'err', text: err instanceof Error ? err.message : t('common.networkError') });
    } finally {
      setPrefSaving(false);
    }
  };

  /* ─── Salva dati personali ─── */
  const handleSaveDati = async (e: React.FormEvent) => {
    e.preventDefault();
    setDatiSaving(true);
    setDatiMsg(null);

    try {
      const res = await fetch('/api/v1/auth/me', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          nome: dati.nome || undefined,
          cognome: dati.cognome || undefined,
          telefono: dati.telefono || null,
          indirizzo: dati.indirizzo || null,
          civico: dati.civico || null,
          cap: dati.cap || null,
          citta: dati.citta || null,
          provincia: dati.provincia || null,
          data_nascita: dati.data_nascita || null,
          codice_fiscale: dati.codice_fiscale || null,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { detail?: string }).detail ?? `Errore ${res.status}`);
      }

      await reload();
      setDatiMsg({ type: 'ok', text: t('profile.dataUpdated') });
    } catch (err) {
      setDatiMsg({ type: 'err', text: err instanceof Error ? err.message : t('common.networkError') });
    } finally {
      setDatiSaving(false);
    }
  };

  /* ─── Cambio password ─── */
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdMsg(null);

    if (pwd.nuova_password !== pwd.conferma_password) {
      setPwdMsg({ type: 'err', text: t('profile.passwordMismatch') });
      return;
    }
    if (pwd.nuova_password.length < 8) {
      setPwdMsg({ type: 'err', text: t('profile.passwordTooShort') });
      return;
    }

    setPwdSaving(true);

    try {
      const res = await fetch('/api/v1/utenti/me/password', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          password_attuale: pwd.password_attuale,
          nuova_password: pwd.nuova_password,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { detail?: string }).detail ?? `Errore ${res.status}`);
      }

      setPwd(EMPTY_PASSWORD);
      setPwdMsg({ type: 'ok', text: t('profile.passwordUpdated') });
    } catch (err) {
      setPwdMsg({ type: 'err', text: err instanceof Error ? err.message : t('common.networkError') });
    } finally {
      setPwdSaving(false);
    }
  };

  const handleDatiChange = (field: keyof DatiForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setDati((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handlePwdChange = (field: keyof PasswordForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setPwd((prev) => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <h1
        className="font-outfit"
        style={{ fontSize: 24, fontWeight: 600, color: 'var(--color-foreground)', marginBottom: 8 }}
      >
        {t('profile.title')}
      </h1>
      <p style={{ fontSize: 14, color: 'var(--color-muted-foreground)', marginBottom: 24 }}>
        {user.email} - {user.ruolo}
        {user.gruppi.length > 0 && ` - ${user.gruppi.map((g) => g.nome).join(', ')}`}
      </p>

      {/* Avatar */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-foreground)', marginBottom: 16 }}>
          {t('profile.avatar')}
        </h2>
        <AvatarUploader
          token={token}
          utenteId={user.id}
          avatarDocumentoId={avatarDocId}
          nome={dati.nome}
          cognome={dati.cognome}
          onChange={async (id) => { setAvatarDocId(id); await reload(); }}
          hint={t('profile.avatarHint')}
          uploadLabel={t('profile.uploadAvatar')}
        />
      </div>

      {/* Preferenze */}
      <div style={{ ...cardStyle, marginTop: 24 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-foreground)', marginBottom: 16 }}>
          {t('profile.preferences')}
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={labelStyle}>{t('common.language')}</label>
            <select style={inputStyle} value={lingua} onChange={(e) => setLingua(e.target.value)}>
              {LINGUE_SUPPORTATE.map((l) => (
                <option key={l} value={l}>{LINGUA_LABEL[l] ?? l}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>{t('common.timezone')}</label>
            <select style={inputStyle} value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              {TIMEZONE_OPZIONI.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
          <div>
            <label style={labelStyle}>Menu laterale</label>
            <select style={inputStyle} value={menuMode} onChange={(e) => setMenuMode(e.target.value)}>
              {MENU_MODE_OPZIONI.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Riquadro di lettura email</label>
            <select style={inputStyle} value={readingPane} onChange={(e) => setReadingPane(e.target.value)}>
              <option value="destra">A destra</option>
              <option value="sotto">Sotto l'elenco</option>
              <option value="nascosto">Nascosto (apertura a tutta pagina)</option>
            </select>
          </div>
        </div>
        {prefMsg && (
          <p
            style={{
              marginTop: 12, padding: '8px 12px', borderRadius: 6, fontSize: 13,
              backgroundColor: prefMsg.type === 'ok' ? 'var(--color-accent-muted, rgba(201,149,42,0.1))' : 'rgba(220,38,38,0.1)',
              color: prefMsg.type === 'ok' ? 'var(--color-accent, #C9952A)' : 'var(--color-destructive, #DC2626)',
            }}
          >
            {prefMsg.text}
          </p>
        )}
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleSavePreferenze}
            disabled={prefSaving}
            className="transition-colors duration-150"
            style={{
              padding: '8px 20px', fontSize: 14, fontWeight: 500, borderRadius: 6,
              backgroundColor: '#1B3A6B', color: '#FFFFFF', border: 'none',
              cursor: prefSaving ? 'not-allowed' : 'pointer', opacity: prefSaving ? 0.6 : 1,
            }}
          >
            {prefSaving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>

      {/* ═══ DATI PERSONALI ═══ */}
      <form onSubmit={handleSaveDati} style={cardStyle}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-foreground)', marginBottom: 16 }}>
          {t('profile.personalData')}
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={labelStyle}>{t('profile.name')} *</label>
            <input style={inputStyle} value={dati.nome} onChange={handleDatiChange('nome')} required />
          </div>
          <div>
            <label style={labelStyle}>{t('profile.surname')} *</label>
            <input style={inputStyle} value={dati.cognome} onChange={handleDatiChange('cognome')} required />
          </div>
          <div>
            <label style={labelStyle}>{t('profile.phone')}</label>
            <input style={inputStyle} value={dati.telefono} onChange={handleDatiChange('telefono')} />
          </div>
          <div>
            <label style={labelStyle}>{t('profile.fiscalCode')}</label>
            <input
              style={inputStyle}
              value={dati.codice_fiscale}
              onChange={handleDatiChange('codice_fiscale')}
              maxLength={16}
            />
          </div>
          <div>
            <label style={labelStyle}>{t('profile.birthDate')}</label>
            <input style={inputStyle} type="date" value={dati.data_nascita} onChange={handleDatiChange('data_nascita')} />
          </div>
          <div>{/* spacer */}</div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>{t('profile.address')}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 8 }}>
              <input style={inputStyle} value={dati.indirizzo} onChange={handleDatiChange('indirizzo')} placeholder="Via / Piazza" />
              <input style={inputStyle} value={dati.civico} onChange={handleDatiChange('civico')} placeholder="N." />
            </div>
          </div>
          <div>
            <label style={labelStyle}>CAP</label>
            <input style={inputStyle} value={dati.cap} onChange={handleDatiChange('cap')} maxLength={5} />
          </div>
          <div>
            <label style={labelStyle}>{t('profile.city')}</label>
            <input style={inputStyle} value={dati.citta} onChange={handleDatiChange('citta')} />
          </div>
          <div>
            <label style={labelStyle}>{t('profile.province')}</label>
            <input style={inputStyle} value={dati.provincia} onChange={handleDatiChange('provincia')} maxLength={2} />
          </div>
        </div>

        {datiMsg && (
          <p
            style={{
              marginTop: 12,
              padding: '8px 12px',
              borderRadius: 6,
              fontSize: 13,
              backgroundColor: datiMsg.type === 'ok' ? 'var(--color-accent-muted, rgba(201,149,42,0.1))' : 'rgba(220,38,38,0.1)',
              color: datiMsg.type === 'ok' ? 'var(--color-accent, #C9952A)' : 'var(--color-destructive, #DC2626)',
            }}
          >
            {datiMsg.text}
          </p>
        )}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            disabled={datiSaving}
            className="transition-colors duration-150"
            style={{
              padding: '8px 20px',
              fontSize: 14,
              fontWeight: 500,
              borderRadius: 6,
              backgroundColor: '#1B3A6B',
              color: '#FFFFFF',
              border: 'none',
              cursor: datiSaving ? 'not-allowed' : 'pointer',
              opacity: datiSaving ? 0.6 : 1,
            }}
          >
            {datiSaving ? t('common.saving') : t('profile.saveData')}
          </button>
        </div>
      </form>

      {/* ═══ CAMBIO PASSWORD ═══ */}
      <form onSubmit={handleChangePassword} style={{ ...cardStyle, marginTop: 24 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-foreground)', marginBottom: 16 }}>
          {t('profile.changePassword')}
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div>
            <label style={labelStyle}>{t('profile.currentPassword')}</label>
            <input
              style={inputStyle}
              type="password"
              autoComplete="current-password"
              value={pwd.password_attuale}
              onChange={handlePwdChange('password_attuale')}
              required
            />
          </div>
          <div>
            <label style={labelStyle}>{t('profile.newPassword')}</label>
            <input
              style={inputStyle}
              type="password"
              autoComplete="new-password"
              value={pwd.nuova_password}
              onChange={handlePwdChange('nuova_password')}
              required
              minLength={8}
            />
          </div>
          <div>
            <label style={labelStyle}>{t('profile.confirmPassword')}</label>
            <input
              style={inputStyle}
              type="password"
              autoComplete="new-password"
              value={pwd.conferma_password}
              onChange={handlePwdChange('conferma_password')}
              required
              minLength={8}
            />
          </div>
        </div>

        {pwdMsg && (
          <p
            style={{
              marginTop: 12,
              padding: '8px 12px',
              borderRadius: 6,
              fontSize: 13,
              backgroundColor: pwdMsg.type === 'ok' ? 'var(--color-accent-muted, rgba(201,149,42,0.1))' : 'rgba(220,38,38,0.1)',
              color: pwdMsg.type === 'ok' ? 'var(--color-accent, #C9952A)' : 'var(--color-destructive, #DC2626)',
            }}
          >
            {pwdMsg.text}
          </p>
        )}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            disabled={pwdSaving}
            className="transition-colors duration-150"
            style={{
              padding: '8px 20px',
              fontSize: 14,
              fontWeight: 500,
              borderRadius: 6,
              backgroundColor: '#1B3A6B',
              color: '#FFFFFF',
              border: 'none',
              cursor: pwdSaving ? 'not-allowed' : 'pointer',
              opacity: pwdSaving ? 0.6 : 1,
            }}
          >
            {pwdSaving ? t('profile.updatingPassword') : t('profile.updatePassword')}
          </button>
        </div>
      </form>

      {/* Sicurezza: 2FA */}
      <TwoFactorSettings />
    </div>
  );
};

export default ProfiloUtente;
